"use client";
import { createContext, useContext, useState, ReactNode } from "react";

export interface ModalOptions {
  title: string;
  content: ReactNode;
  onClose?: () => void;
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  fullscreen?: boolean;
}

interface ModalContextType {
  modals: ModalOptions[];
  showModal: (options: ModalOptions) => void;
  closeModal: (index: number) => void;
  closeTopModal: () => void;
  size: "sm" | "md" | "lg" | "xl" | "2xl";
}

const ModalContext = createContext<ModalContextType | undefined>(undefined);

export function ModalProvider({ children }: { children: ReactNode }) {
  const [modals, setModals] = useState<ModalOptions[]>([]);

  const showModal = (options: ModalOptions) => {
    setModals((prev) => [...prev, options]);
  };

  const closeModal = (index: number) => {
    setModals((prev) => {
      const modal = prev[index];
      if (modal?.onClose) modal.onClose();
      return prev.filter((_, i) => i !== index);
    });
  };

  const closeTopModal = () => {
    setModals((prev) => {
      if (prev.length === 0) return prev;
      const modal = prev[prev.length - 1];
      if (modal?.onClose) modal.onClose();
      return prev.slice(0, -1);
    });
  };

  return (
    <ModalContext.Provider
      value={{
        modals,
        showModal,
        closeModal,
        closeTopModal,
        size: modals[modals.length - 1]?.size || "xl",
      }}
    >
      {children}
    </ModalContext.Provider>
  );
}

export function useModal() {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a ModalProvider");
  }
  return context;
}
